import React, { useState, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from './contexts/AuthContext';
import { authService } from './api/services/authService';
import { rolePermissionService } from './api/services/rolePermissionService';
import { dineOpsService } from './api/services/dineOpsService';
import { configService } from './api/services/configService';
import { menuModulesForSwitches } from './config/moduleSwitches';
import type { DineOutletBrief } from './api/types/dineops.types';
import { tokenManager } from './auth/tokenManager';
import { STORAGE_KEYS } from './auth/storageKeys';
import { getActiveDineOutletId, setActiveDineOutlet, clearActiveDineOutlet } from './auth/dineOutlet';
import type { AccessibleTenant } from './api/types/auth.types';
import type { RolePermissionMerged } from './api/types/rolePermission.types';
import {
  getRolePriority,
  getKnownRoleDisplayList,
  filterNavigationMenu,
  applyRoleActions,
  type NavMenuSection,
  type ModulesConfigMap,
  type AppRole,
} from './config/rolePermissions';
import { hydrateKnownRoles } from './auth/knownRolesBoot';

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginProps {
  onLoginSuccess?: (user: any) => void;
}

type AuthStep = 'credentials' | 'tenant-select';

// ============================================================================
// APP CONFIG - Application settings (title, etc.)
// Defined at module level so it can be used for both display and localStorage
// ============================================================================
const appConfig = {
  appTitle: 'Central Reservation System'  // This will appear in header (top-left), login page, and welcome message
};

const getAuthMode = (): 'tenant-hub' | 'legacy' => {
  return (process.env.REACT_APP_AUTH_MODE as 'tenant-hub' | 'legacy') || 'legacy';
};

// ============================================================================
// JWT ROLE EXTRACTION — Walks every place a Keycloak token may carry roles:
//   - resource_access.crs.roles[]   (Keycloak client-scoped roles, primary source)
//   - resource_access.<APP_ID>.roles[] (when REACT_APP_APPLICATION_ID is set)
//   - realm_access.roles[]          (Keycloak realm roles)
//   - flat `role` / `roles` claim    (custom JWT shapes)
//   - authData.userRole              (TenantHub-shaped response field)
// Returns a deduped, lowercased list. Empty array means "no roles found".
// ============================================================================
const collectUserRoles = (userRoleField?: string | null): string[] => {
  const out = new Set<string>();
  const push = (v: unknown) => {
    if (typeof v !== 'string') return;
    const trimmed = v.trim();
    if (trimmed) out.add(trimmed.toLowerCase());
  };

  // 1. authData.userRole — may be a single role or comma/space separated.
  if (userRoleField) {
    userRoleField.split(/[\s,;]+/).forEach(push);
  }

  // 2. JWT claims.
  try {
    const token = tokenManager.getAccessToken();
    if (token) {
      const payload = tokenManager.decodeToken(token) as any;
      if (payload) {
        // resource_access.<client>.roles[]
        const ra = payload.resource_access ?? {};
        const appId = process.env.REACT_APP_APPLICATION_ID;
        const candidates = ['crs', appId, 'account'].filter(Boolean) as string[];
        for (const client of candidates) {
          const rolesArr = ra[client]?.roles;
          if (Array.isArray(rolesArr)) rolesArr.forEach(push);
        }
        // Any other client buckets (best-effort — useful for custom realms)
        for (const k of Object.keys(ra)) {
          const rolesArr = ra[k]?.roles;
          if (Array.isArray(rolesArr)) rolesArr.forEach(push);
        }
        // realm_access.roles[]
        if (Array.isArray(payload.realm_access?.roles)) {
          payload.realm_access.roles.forEach(push);
        }
        // Flat claims
        if (Array.isArray(payload.roles)) payload.roles.forEach(push);
        if (typeof payload.role === 'string') push(payload.role);
      }
    }
  } catch {
    // Token decode failed — ignore; we still have userRoleField.
  }

  return Array.from(out);
};

/**
 * Highest-priority CRS role from a list, or null if none of the user's roles
 * is a CRS role. Uses the DB-driven catalog (via getRolePriority) so any role
 * added via the Role Permissions admin screen is recognised at the very next
 * login — no code deploy required. Falls back to the four baseline codes only
 * when the /known-roles cache is missing entirely (pre-boot / storage disabled).
 */
const pickHighestPriorityRole = (roles: ReadonlyArray<string>): AppRole | null => {
  const set = new Set(roles.map((r) => r.toLowerCase()));
  const priorityList = getRolePriority();
  for (const candidate of priorityList) {
    if (set.has(candidate.toLowerCase())) return candidate;
  }
  return null;
};

const LoginInterface: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [isPending, startTransition] = useTransition();
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  // Tenant-Hub auth flow state
  const [authStep, setAuthStep] = useState<AuthStep>('credentials');

  /**
   * DineOps multi-outlet (O9): sets the outlet the Restaurant screens start in.
   * Nobody is asked here: the last outlet is kept when still allowed, else the
   * first one. The user switches outlet in the header (main_layout). A user
   * with no Restaurant screens, or a tenant without DineOps, skips this.
   */
  const pickDineOutlet = async (visibleModules: readonly string[]): Promise<void> => {
    if (!visibleModules.some((m) => m.startsWith('dineops-'))) return;
    let outlets: DineOutletBrief[] = [];
    try {
      outlets = await dineOpsService.getMyOutlets();
    } catch {
      return; // DineOps not installed or switched off
    }
    const last = getActiveDineOutletId();
    const o = outlets.find((x) => x.id === last) ?? outlets[0];
    if (o) setActiveDineOutlet(o.id, o.outletName);
    else clearActiveDineOutlet();
  };
  const [accessibleTenants, setAccessibleTenants] = useState<AccessibleTenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedAppId, setSelectedAppId] = useState<string>('');

  const authContext = useAuthContext();
  const navigate = useNavigate();

  // Real-time email validation (only checks if not empty)
  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError('User ID is required');
      return false;
    }
    setEmailError('');
    return true;
  };

  // Real-time password validation
  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    setPasswordError('');
    return true;
  };

  // Handle tenant-hub discover step
  const handleTenantHubDiscover = async () => {
    setError('');
    const isEmailValid = validateEmail(credentials.email);
    const isPasswordValid = validatePassword(credentials.password);
    if (!isEmailValid || !isPasswordValid) return;

    startTransition(async () => {
      try {
        const discoveryResponse = await authContext.discover(
          credentials.email,
          credentials.password,
          process.env.REACT_APP_APPLICATION_ID // sent as clientId to filter apps
        );

        const tenants = discoveryResponse.accessibleTenants;

        if (tenants.length === 0) {
          setError('No accessible tenants found for your account.');
          return;
        }

        // If only one tenant with one app, auto-proceed
        if (tenants.length === 1 && tenants[0].applications.length === 1) {
          await handleTenantHubAuthenticate(
            tenants[0].tenantId,
            tenants[0].applications[0].applicationId
          );
          return;
        }

        // Show tenant selection
        setAccessibleTenants(tenants);
        // Pre-select recommended tenant or first
        const recommended = discoveryResponse.recommendedTenant;
        if (recommended) {
          setSelectedTenantId(recommended.tenantId);
          if (recommended.applications.length === 1) {
            setSelectedAppId(recommended.applications[0].applicationId);
          }
        } else if (tenants.length > 0) {
          setSelectedTenantId(tenants[0].tenantId);
          if (tenants[0].applications.length === 1) {
            setSelectedAppId(tenants[0].applications[0].applicationId);
          }
        }
        setAuthStep('tenant-select');
      } catch (err: any) {
        setError(err.message || 'Discovery failed. Please check your credentials.');
      }
    });
  };

  // Handle tenant-hub authenticate step
  const handleTenantHubAuthenticate = async (tenantId?: string, appId?: string) => {
    const tId = tenantId || selectedTenantId;
    const aId = appId || selectedAppId;

    if (!tId || !aId) {
      setError('Please select a tenant and application.');
      return;
    }

    startTransition(async () => {
      try {
        await authContext.authenticate(
          credentials.email,
          credentials.password,
          tId,
          aId
        );

        localStorage.setItem('APP_CONFIG', JSON.stringify(appConfig));
        const proceedHub = () => {
          navigate('/home', { replace: true });
          onLoginSuccess?.({ username: credentials.email });
        };
        let hubModules: string[] = [];
        try { hubModules = JSON.parse(localStorage.getItem(STORAGE_KEYS.RBAC_VISIBLE_MODULES) || '[]'); } catch { hubModules = []; }
        await pickDineOutlet(hubModules);
        proceedHub();
      } catch (err: any) {
        setError(err.message || 'Authentication failed. Please try again.');
      }
    });
  };

  const handleSubmit = () => {
    setError('');

    // Validate both fields
    const isEmailValid = validateEmail(credentials.email);
    const isPasswordValid = validatePassword(credentials.password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    // === Tenant-Hub Mode ===
    if (getAuthMode() === 'tenant-hub') {
      if (authStep === 'credentials') {
        handleTenantHubDiscover();
      } else {
        handleTenantHubAuthenticate();
      }
      return;
    }

    // === Legacy Mode — Real API (discover → authenticate, same as LMMS) ===
    startTransition(async () => {
      try {
        const APPLICATION_ID = process.env.REACT_APP_APPLICATION_ID || '';

        // Ensure X-App-ID header is available for auth interceptor
        tokenManager.setAppId(APPLICATION_ID);

        // Step 1: Discover accessible tenants
        const discoverResponse = await authService.discover({
          username: credentials.email,
          password: credentials.password,
          clientId: APPLICATION_ID,
        });

        if (!discoverResponse.success || !discoverResponse.data) {
          setError(discoverResponse.message || 'Invalid credentials. Please try again.');
          return;
        }

        const { accessibleTenants, recommendedTenant } = discoverResponse.data;

        if (!accessibleTenants || accessibleTenants.length === 0) {
          setError(
            'Your account is not assigned to this application. ' +
            'Please contact your administrator to subscribe the CRS application to your tenant.'
          );
          return;
        }

        // Step 2: Auto-select tenant (single tenant → proceed, multiple → use recommended/first)
        const selectedTenant = recommendedTenant || accessibleTenants[0];
        const selectedApp = selectedTenant.applications.find(
          a => a.applicationId === APPLICATION_ID || a.clientId === APPLICATION_ID
        ) || selectedTenant.applications[0];

        if (!selectedApp) {
          setError(
            'CRS application is not available in your tenant. ' +
            'Please contact your administrator to configure application access.'
          );
          return;
        }

        // Step 3: Authenticate with selected tenant + app
        const authResponse = await authService.authenticate({
          username: credentials.email,
          password: credentials.password,
          selectedTenantId: selectedTenant.tenantId,
          selectedApplicationId: selectedApp.applicationId,
        });

        if (!authResponse.success || !authResponse.data) {
          setError(authResponse.message || 'Authentication failed. Please try again.');
          return;
        }

        const authData = authResponse.data;

        // Build user object for legacy compatibility
        const displayName = [authData.userFirstName, authData.userLastName]
          .filter(Boolean).join(' ') || authData.userName || credentials.email;
        const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

        const user = {
          id: authData.userId || '',
          name: displayName,
          role: authData.userRole || 'User',
          email: authData.userEmail || credentials.email,
          avatar: initials,
          permissions: ['read', 'write', 'admin'],
        };

        localStorage.setItem('user', JSON.stringify(user));

        // ============================================================================
        // NAVIGATION MENU - Define sidebar menu structure (hardcoded for legacy mode)
        // ============================================================================
        const navigationMenu = [
          // ==================================================================
          // Menu layout (2026-10-27, crs-doc CRS-Setup-Navigation-and-Help-Design.md):
          // daily work first, then Masters (added to all year), Reports, and
          // one Setup area in the order a new property is set up. Item ids are
          // module codes (admin.tbl_role_permissions.visible_modules) and did
          // not change; group ids ("*-group-*") are not module codes:
          // filterNavigationMenu keeps a group while the role sees any screen
          // in it. Each moved screen's breadcrumb in modulesConfig follows it.
          // ==================================================================
          {
            "section": "Dashboard",
            "icon": "layout",
            "items": [
              { "id": "overview", "title": "Overview", "icon": "home", "active": true },
              { "id": "dashboard", "title": "Dashboard", "icon": "layout", "active": true }
            ]
          },
          {
            // Reservations and the stay. The `front-office` id is kept to avoid
            // RBAC/menu-cache churn; future PMS screens land here with pms-* ids.
            "section": "Front Office",
            "icon": "calendar",
            "items": [
              { "id": "booking-list",  "title": "Booking",      "icon": "clipboardList", "active": true },
              { "id": "availability",  "title": "Availability", "icon": "calendar", "active": true },
              { "id": "shortage-board", "title": "Shortage Board", "icon": "alertTriangle", "active": true },
              { "id": "front-office",  "title": "Front Office", "icon": "home",     "active": true },
              { "id": "pms-inhouse",   "title": "In-House Guests", "icon": "users", "active": true },
              { "id": "pms-invoices", "title": "Invoices", "icon": "file", "active": true },
              { "id": "pms-night-audit", "title": "Night Audit",   "icon": "lock",  "active": true },
              { "id": "audit-corrections", "title": "Audit Corrections", "icon": "edit", "active": true },
              { "id": "fo-closing", "title": "Front Office Closing", "icon": "lock", "active": true },
            ]
          },
          // ==================================================================
          // DineOps (restaurant). The item ids ARE the module codes in
          // admin.tbl_role_permissions.visible_modules — filterNavigationMenu
          // matches on them — and they must equal
          // crs.Core/Constants/DineOpsModule/DineOpsPermissions.Modules and
          // components/modules/dineops-module/dineOpsScreens.ts. Three files,
          // one vocabulary; DineOpsIsolationTests fails the build on a literal
          // typed anywhere but the constants file.
          //
          // Only screens with a CRS-shell route appear here. The waiter app is a
          // separate PWA, so dineops-orders is a module code with no menu entry.
          // The kitchen display (dineops-kds) opens here as a tab, and its Full
          // screen button gives the wall page /kitchen-display for the kitchen TV.
          // Settlement (BILL-002) is deliberately absent too — BR-035: it is the
          // detail view of ONE bill, and a menu entry would have to guess which.
          // The restaurant's reports are under Reports, its setup under Setup.
          // ==================================================================
          {
            "section": "Restaurant",
            "icon": "coffee",
            "items": [
              { "id": "dineops-kds",          "title": "Kitchen Display", "icon": "monitor",    "active": true },
              // Open / close kitchen stations during service; they are set up on Setup → Restaurant → Kitchens.
              { "id": "dineops-departments",  "title": "Kitchen Stations", "icon": "flame", "active": true },
              // KOT printing: the Print module's live queue (failed / waiting tickets), next to the kitchen.
              { "id": "print-queue",          "title": "Print Queue",     "icon": "printer",    "active": true },
              // Department requisitions (R1): NC KOTs for welcome drinks, staff meals; issued at transfer value, never billed.
              { "id": "dineops-requisitions", "title": "Requisitions",    "icon": "clipboardList", "active": true },
              { "id": "dineops-specials",     "title": "Today's Special", "icon": "star",     "active": true },
              { "id": "dineops-bills",        "title": "Bill Queue",      "icon": "receipt", "active": true },
              { "id": "dineops-shifts",       "title": "Shifts",          "icon": "clock",    "active": true },
              { "id": "dineops-review",       "title": "Corrections",     "icon": "edit", "active": true },
              { "id": "dineops-day-close",    "title": "Day Close",       "icon": "moon",     "active": true },
            ]
          },
          {
            "section": "Approvals",
            "icon": "checkCircle",
            "items": [
              { "id": "approval-inbox", "title": "Approval Inbox", "icon": "checkCircle", "active": true },
            ]
          },
          {
            // Masters added to all year; one-time setup is under Setup.
            "section": "Masters",
            "icon": "folder",
            "items": [
              { "id": "guest-master",     "title": "Guest",     "icon": "user",       "active": true },
              { "id": "agent-master",     "title": "Agent",     "icon": "user",       "active": true },
              { "id": "ota-master",       "title": "OTA",       "icon": "globe",       "active": true },
              { "id": "corporate-master", "title": "Corporate", "icon": "briefcase",     "active": true },
              { "id": "rate-plan-master", "title": "Rate Plan", "icon": "dollarSign", "active": true },
            ]
          },
          {
            "section": "Reports",
            "icon": "chart",
            "items": [
              { "id": "reports-group-front-office", "title": "Front Office", "icon": "calendar", "active": true, "children": [
                { "id": "booking-register-report",       "title": "Booking Register",        "icon": "fileText",   "active": true },
                { "id": "occupancy-chart-report",        "title": "Room Occupancy Chart",    "icon": "grid",       "active": true },
                { "id": "forward-occupancy-report",      "title": "Forward Occupancy",       "icon": "calendar",   "active": true },
                { "id": "checkin-details-report",        "title": "Check-in Details",        "icon": "logIn",      "active": true },
                { "id": "checkout-register",             "title": "Checkout Register",       "icon": "logOut",     "active": true },
                { "id": "daily-monthly-summary-report",  "title": "Daily / Monthly Summary", "icon": "chart",      "active": true },
                { "id": "agent-corporate-report",        "title": "Agent / Corporate",       "icon": "briefcase",       "active": true },
                { "id": "complimentary-report",          "title": "Complimentary",           "icon": "gift",        "active": true },
                { "id": "discount-report",               "title": "Discount",                "icon": "percent", "active": true },
                { "id": "min-rate-violation-report",     "title": "Minimum Rate Violation",  "icon": "alertTriangle","active": true },
                { "id": "waiver-report",                 "title": "Relaxations & Waivers",   "icon": "list",       "active": true },
                { "id": "meal-plan-revenue-report",      "title": "Meal Plan Revenue",       "icon": "utensils",        "active": true },
                { "id": "meal-forecast-report",          "title": "Meal Forecast",           "icon": "users",      "active": true },
                { "id": "unused-meal-report",            "title": "Unused Package Meals",    "icon": "list",       "active": true },
                { "id": "extra-pax-report",              "title": "Extra Pax",               "icon": "userPlus",       "active": true },
              ] },
              { "id": "reports-group-collections", "title": "Collections", "icon": "currency", "active": true, "children": [
                { "id": "collection-report",             "title": "Collection",              "icon": "currency",   "active": true },
                { "id": "fo-collection-report",          "title": "Front Office Collection", "icon": "currency",   "active": true },
                { "id": "advance-payments",              "title": "Advance Payments",        "icon": "creditCard", "active": true },
                { "id": "guest-ledger-report",           "title": "Guest Ledger",            "icon": "list",       "active": true },
                { "id": "posting-register",              "title": "Posting Register",        "icon": "currency",   "active": true },
              ] },
              { "id": "reports-group-restaurant", "title": "Restaurant", "icon": "coffee", "active": true, "children": [
                { "id": "dineops-dashboard-report", "title": "Dashboard",   "icon": "pieChart", "active": true },
                { "id": "dineops-sales-report", "title": "Sales Report",    "icon": "trendingUp", "active": true },
                { "id": "dineops-settlement-report", "title": "Settlement", "icon": "creditCard", "active": true },
                { "id": "dineops-wastage-report", "title": "Wastage",       "icon": "trash", "active": true },
                { "id": "dineops-complaints-report", "title": "Complaints Report", "icon": "messageSquare", "active": true },
                { "id": "dineops-kitchen-report", "title": "Kitchen Performance", "icon": "activity", "active": true },
                { "id": "dineops-requisition-report", "title": "Requisition Register", "icon": "clipboardList", "active": true },
                { "id": "dineops-bill-register", "title": "Bill Register", "icon": "fileText", "active": true },
              ] },
            ]
          },
          {
            // Everything set once before operating, in setup order. The Setup
            // Checklist and the Configuration Report join this section next.
            "section": "Setup",
            "icon": "settings",
            "items": [
              // What is still missing before operating (design section 5); first, so a new tenant starts here.
              { "id": "setup-checklist",       "title": "Setup Checklist", "icon": "checkCircle", "active": true },
              { "id": "setup-group-organisation", "title": "Organisation", "icon": "briefcase", "active": true, "children": [
                { "id": "org-entity",            "title": "Entities",        "icon": "briefcase", "active": true },
                { "id": "org-business-unit",     "title": "Business Units",  "icon": "folder",    "active": true },
                { "id": "org-financial-year",    "title": "Financial Years", "icon": "calendar",  "active": true },
              ] },
              { "id": "setup-group-property", "title": "Property & Rooms", "icon": "building", "active": true, "children": [
                { "id": "property-master",       "title": "Property",        "icon": "building",     "active": true },
                { "id": "room-category-master",  "title": "Room Category",   "icon": "layers",      "active": true },
                { "id": "room-master",           "title": "Room",            "icon": "bed",     "active": true },
                { "id": "meal-plan-master",      "title": "Meal Plan",       "icon": "utensils",      "active": true },
                { "id": "addon-master",          "title": "Add-on",          "icon": "gift",     "active": true },
                { "id": "property-terms-master", "title": "Property Terms",  "icon": "fileText", "active": true },
                { "id": "bank-account-master",   "title": "Bank Account",    "icon": "landmark", "active": true },
              ] },
              { "id": "setup-group-tax-billing", "title": "Tax & Billing", "icon": "percent", "active": true, "children": [
                { "id": "tax-master",            "title": "Tax Masters",     "icon": "percent",  "active": true },
                { "id": "tax-registrations",     "title": "Tax Registrations", "icon": "briefcase", "active": true },
                { "id": "pms-charge-type",       "title": "Charge Types",    "icon": "tag",      "active": true },
                { "id": "tax-charge-mapping",    "title": "Charge Tax Mappings", "icon": "receipt", "active": true },
                { "id": "pms-document-series",   "title": "Document Series", "icon": "fileText", "active": true },
                { "id": "pms-print-layouts",     "title": "Print Layouts",   "icon": "printer",  "active": true },
                { "id": "ledger-mapping",        "title": "Ledger Mapping",  "icon": "chartofAccount", "active": true },
              ] },
              // Settings (the template's one Settings screen): module switches, booking, front office,
              // billing, notifications… config framework groups by setup area.
              { "id": "config-settings",       "title": "Settings",        "icon": "sliders", "active": true },
              { "id": "setup-group-restaurant", "title": "Restaurant", "icon": "coffee", "active": true, "children": [
                { "id": "dineops-outlets",      "title": "Outlets",         "icon": "store",     "active": true },
                { "id": "dineops-kitchens",     "title": "Kitchens",        "icon": "utensils", "active": true },
                { "id": "dineops-sections",     "title": "Sections",        "icon": "map",     "active": true },
                { "id": "dineops-tables",       "title": "Tables",          "icon": "table",     "active": true },
                { "id": "dineops-billing-points", "title": "Billing Points", "icon": "printer", "active": true },
                { "id": "dineops-approval-rules", "title": "Approval Rules", "icon": "checkCircle", "active": true },
                { "id": "dineops-menu",         "title": "Menu Items",      "icon": "utensils",     "active": true },
                { "id": "dineops-outlet-items", "title": "Outlet Items",    "icon": "clipboardList",     "active": true },
                { "id": "dineops-price-board",  "title": "Price Board",     "icon": "dollarSign",      "active": true },
                { "id": "dineops-addon-groups", "title": "Add-on Groups",   "icon": "layers",      "active": true },
                { "id": "dineops-tags",         "title": "Tags",            "icon": "tag",      "active": true },
                { "id": "dineops-price-lists",  "title": "Price Lists",     "icon": "dollarSign",      "active": true },
                { "id": "dineops-quick-notes",  "title": "Quick Notes",     "icon": "stickyNote",     "active": true },
                { "id": "dineops-charges",      "title": "Charges",         "icon": "truck", "active": false },
              ] },
              // KOT printing (Print module, crs-db fixes/060): the printers and the agents that reach them.
              { "id": "setup-group-printing", "title": "Printing", "icon": "printer", "active": true, "children": [
                { "id": "print-printers",       "title": "Printers",        "icon": "printer",    "active": true },
                { "id": "print-agents",         "title": "Print Agents",    "icon": "shield",     "active": true },
              ] },
              { "id": "role-permissions", "title": "Role Permissions", "icon": "shield", "active": true },
              // Every setting for review and sign-off, last in Setup (design S8, section 6).
              { "id": "setup-config-report",  "title": "Configuration Report", "icon": "fileText", "active": true },
            ]
          },
        ];

        // ============================================================================
        // MODULES CONFIG - Define component mapping for each menu item
        // ============================================================================
        const modulesConfig = {
          // ---- DineOps (restaurant). Only the three screens cycle 1 builds have
          // a componentName; the rest are menu entries with active:false until
          // their own cycle supplies a body, so a user cannot open a blank page.
          'dineops-menu': {
            showHeader: false,
            title: 'Menu Items',
            description: 'The menu, kept once for every outlet: categories, groups, dishes, variants and add-ons',
            componentName: 'DineOpsMenuItemsScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Menu Items']
          },
          'dineops-outlet-items': {
            showHeader: false,
            title: 'Outlet Items',
            description: 'The dishes the outlet in the header sells, at its own price',
            componentName: 'DineOpsOutletItemsScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Outlet Items']
          },
          'dineops-price-board': {
            showHeader: false,
            title: 'Price Board',
            description: 'The price of every dish in every outlet, on one screen, with bulk change',
            componentName: 'DineOpsPriceBoardScreen',
            actions: ['view', 'edit', 'export'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Price Board']
          },
          'dineops-addon-groups': {
            showHeader: false,
            title: 'Add-on Groups',
            description: 'Sets of extras, and the menu groups that use each',
            componentName: 'DineOpsAddonGroupsScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Add-on Groups']
          },
          'dineops-dashboard-report': {
            showHeader: false, title: 'Dashboard', description: 'Today, now: covers, revenue, what is late',
            componentName: 'DineOpsDashboardScreen', actions: ['view'], permissions: ['admin', 'manager'], breadcrumb: ['Reports', 'Restaurant', 'Dashboard']
          },
          'dineops-settlement-report': {
            showHeader: false, title: 'Settlement', description: 'Receipts by method reconciled to shifts; revenue summary for night audit',
            componentName: 'DineOpsSettlementReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager'], breadcrumb: ['Reports', 'Restaurant', 'Settlement']
          },
          'dineops-wastage-report': {
            showHeader: false, title: 'Wastage', description: 'Dishes cancelled after acceptance, with reasons',
            componentName: 'DineOpsWastageReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager'], breadcrumb: ['Reports', 'Restaurant', 'Wastage']
          },
          'dineops-complaints-report': {
            showHeader: false, title: 'Complaints Report', description: 'Complaints by dish, station and waiter',
            componentName: 'DineOpsComplaintsReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager'], breadcrumb: ['Reports', 'Restaurant', 'Complaints Report']
          },
          'dineops-kitchen-report': {
            showHeader: false, title: 'Kitchen Performance', description: 'Ticket times against target by station',
            componentName: 'DineOpsKitchenReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager'], breadcrumb: ['Reports', 'Restaurant', 'Kitchen Performance']
          },
          'dineops-requisition-report': {
            showHeader: false, title: 'Requisition Register', description: 'What departments took from the kitchens at transfer value, by issue date',
            componentName: 'DineOpsRequisitionRegisterScreen', actions: ['view', 'export'], permissions: ['admin', 'manager'], breadcrumb: ['Reports', 'Restaurant', 'Requisition Register']
          },
          'dineops-bill-register': {
            showHeader: false, title: 'Bill Register', description: 'Every bill of a period: the register auditors and accounts work from',
            componentName: 'DineOpsBillRegisterScreen', actions: ['view', 'export'], permissions: ['admin', 'manager'], breadcrumb: ['Reports', 'Restaurant', 'Bill Register']
          },
          'dineops-requisitions': {
            showHeader: false,
            title: 'Requisitions',
            description: 'Food and drink departments take from an outlet\'s kitchen at transfer value; never billed',
            componentName: 'DineOpsRequisitionsScreen',
            actions: ['view', 'create', 'issue', 'cancel', 'purposes'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Restaurant', 'Requisitions']
          },
          'dineops-sales-report': {
            showHeader: false,
            title: 'Sales Report',
            description: 'Invoices by business date; drill down table → order → bill',
            componentName: 'DineOpsSalesReportScreen',
            actions: ['view', 'export', 'print'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Reports', 'Restaurant', 'Sales Report']
          },
          'dineops-shifts': {
            showHeader: false,
            title: 'Shifts',
            description: 'Cashier shifts: float, declared cash, variance',
            componentName: 'DineOpsShiftsScreen',
            actions: ['view', 'create', 'update'],
            permissions: ['admin', 'manager', 'cashier'],
            breadcrumb: ['Restaurant', 'Shifts']
          },
          'dineops-review': {
            showHeader: false,
            title: 'Corrections',
            description: 'Review discounts, voids, refunds and open items; resolve complaints',
            componentName: 'DineOpsCorrectionsScreen',
            actions: ['view', 'approve', 'reject'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Restaurant', 'Corrections']
          },
          'dineops-day-close': {
            showHeader: false,
            title: 'Day Close',
            description: 'Close the business day once every correction is reviewed',
            componentName: 'DineOpsDayCloseScreen',
            actions: ['view', 'day-close'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Restaurant', 'Day Close']
          },
          'dineops-bills': {
            showHeader: false,
            title: 'Bill Queue',
            description: 'Tables waiting for a bill, open bills, settlement and print',
            componentName: 'DineOpsBillQueueScreen',
            actions: ['add', 'edit', 'delete', 'print', 'void', 'discount'],
            permissions: ['admin', 'manager', 'cashier'],
            breadcrumb: ['Restaurant', 'Bill Queue']
          },
          'dineops-price-lists': {
            showHeader: false,
            title: 'Price Lists',
            description: 'A second price for the same dish, by order channel and date',
            componentName: 'DineOpsPriceListsScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Price Lists']
          },
          'dineops-specials': {
            showHeader: false,
            title: "Today's Special",
            description: 'The board: dishes that already exist, on for a run of dates',
            componentName: 'DineOpsSpecialsScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Restaurant', "Today's Special"]
          },
          'dineops-tags': {
            showHeader: false,
            title: 'Tags',
            description: 'Tag groups and tags: the words that describe a dish',
            componentName: 'DineOpsTagsScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Tags']
          },
          'dineops-departments': {
            showHeader: false,
            title: 'Kitchen Stations',
            description: 'Open and close kitchen stations, and their fallback; set them up on Kitchens',
            componentName: 'DineOpsDepartmentsScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Restaurant', 'Kitchen Stations'],
          },
          'dineops-tables': {
            showHeader: false,
            title: 'Tables',
            description: 'Dining tables and their sections',
            componentName: 'DineOpsTablesScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Tables']
          },
          'dineops-sections': {
            showHeader: false,
            title: 'Sections',
            description: 'Areas of the floor',
            componentName: 'DineOpsSectionsScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Sections']
          },
          'dineops-outlets': {
            showHeader: false,
            title: 'Outlets',
            description: 'Outlets: tables, numbering and GSTIN of their own, and the kitchen stations each may use',
            componentName: 'DineOpsOutletsScreen',
            actions: ['add', 'edit'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Restaurant', 'Outlets']
          },
          'dineops-kds': {
            showHeader: false,
            title: 'Kitchen Display',
            description: 'Tickets waiting at the kitchen stations this outlet sends to',
            componentName: 'DineOpsKitchenDisplayScreen',
            actions: ['view', 'update', 'reject'],
            permissions: ['admin', 'manager', 'kitchen'],
            breadcrumb: ['Restaurant', 'Kitchen Display']
          },
          'dineops-kitchens': {
            showHeader: false,
            title: 'Kitchens',
            description: 'Kitchens and their kitchen stations, each kitchen linked to a business unit',
            componentName: 'DineOpsKitchensScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Kitchens']
          },
          'dineops-billing-points': {
            showHeader: false,
            title: 'Billing Points',
            description: 'Cash counters of the outlet; a bill goes to its section\'s counter',
            componentName: 'DineOpsBillingPointsScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Billing Points']
          },
          'dineops-approval-rules': {
            showHeader: false,
            title: 'Approval Rules',
            description: 'Which restaurant approvals are asked, of which role, above what value',
            componentName: 'DineOpsApprovalRulesScreen',
            actions: ['view', 'add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Approval Rules']
          },
          // ---- Print module (KOT printing). Codes match PrintPermissions.Modules and fixes/060.
          'print-printers': {
            showHeader: false,
            title: 'Printers',
            description: 'Kitchen and receipt printers, each reached through a Print Agent',
            componentName: 'PrintersScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Printing', 'Printers']
          },
          'print-agents': {
            showHeader: false,
            title: 'Print Agents',
            description: 'The PCs that print for each site: pair, approve, suspend, revoke',
            componentName: 'PrintAgentsScreen',
            actions: ['add', 'edit', 'manage'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Printing', 'Print Agents']
          },
          'print-queue': {
            showHeader: false,
            title: 'Print Queue',
            description: 'Live print jobs and failures: retry, print elsewhere, cancel',
            componentName: 'PrintQueueScreen',
            actions: ['view', 'update'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Restaurant', 'Print Queue']
          },
          'dineops-quick-notes': {
            showHeader: false,
            title: 'Quick Notes',
            description: 'Short instructions the waiter taps instead of typing',
            componentName: 'DineOpsQuickNotesScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Setup', 'Restaurant', 'Quick Notes']
          },
          'property-master': {
            showHeader: false,
            title: 'Property Master',
            description: 'Manage hotel properties and their configurations',
            componentName: 'PropertyMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Setup', 'Property & Rooms', 'Property']
          },
          'room-category-master': {
            showHeader: false,
            title: 'Room Category Master',
            description: 'Manage room categories and their configurations',
            componentName: 'RoomCategoryMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Setup', 'Property & Rooms', 'Room Category']
          },
          'room-master': {
            showHeader: false,
            title: 'Room Master',
            description: 'Manage rooms and their assignments',
            componentName: 'RoomMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Setup', 'Property & Rooms', 'Room']
          },
          'meal-plan-master': {
            showHeader: false,
            title: 'Meal Plan Master',
            description: 'Manage meal plans and their configurations',
            componentName: 'MealPlanMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Setup', 'Property & Rooms', 'Meal Plan']
          },
          'addon-master': {
            showHeader: false,
            title: 'Add-on Master',
            description: 'Manage ancillary add-on items and services for bookings',
            componentName: 'AddonMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Setup', 'Property & Rooms', 'Add-on']
          },
          'rate-plan-master': {
            showHeader: false,
            title: 'Rate Plan Master',
            description: 'Manage rate plans and pricing configurations',
            componentName: 'RatePlanMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Masters', 'Rate Plan']
          },
          'agent-master': {
            showHeader: false,
            title: 'Agent Master',
            description: 'Manage travel agents and commission configurations',
            componentName: 'AgentMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Masters', 'Agent']
          },
          'ota-master': {
            showHeader: false,
            title: 'OTA Master',
            description: 'Manage OTA (Online Travel Agency) partners',
            componentName: 'OtaMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Masters', 'OTA']
          },
          'corporate-master': {
            showHeader: false,
            title: 'Corporate Master',
            description: 'Manage corporate accounts and discount agreements',
            componentName: 'CorporateMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Masters', 'Corporate']
          },
          'guest-master': {
            showHeader: false,
            title: 'Guest Master',
            description: 'Manage guest profiles and identity records',
            componentName: 'GuestMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Masters', 'Guest']
          },
          'property-terms-master': {
            showHeader: false,
            title: 'Property Terms',
            description: 'Manage property terms and policies for booking confirmations',
            componentName: 'PropertyTermsMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Setup', 'Property & Rooms', 'Property Terms']
          },
          'bank-account-master': {
            showHeader: false,
            title: 'Bank Account Master',
            description: 'Manage receiving bank accounts for booking payments',
            componentName: 'BankAccountMasterScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Setup', 'Property & Rooms', 'Bank Account']
          },
          'setup-config-report': {
            showHeader: false,
            title: 'Configuration Report',
            description: 'Every setting in Setup order and the setup changes since a date, on screen, in Excel or as a PDF for sign-off',
            componentName: 'ConfigReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Configuration Report']
          },
          'setup-checklist': {
            showHeader: false,
            title: 'Setup Checklist',
            description: 'What still needs setting up before the hotel can operate, for the whole tenant',
            componentName: 'SetupChecklistScreen',
            actions: ['view'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Setup Checklist']
          },
          'tax-registrations': {
            showHeader: false,
            title: 'Tax Registrations',
            description: 'The tax jurisdiction of each legal entity; properties and outlets are taxed under it',
            componentName: 'TaxRegistrationsScreen',
            actions: ['edit', 'view'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Tax & Billing', 'Tax Registrations']
          },
          'tax-master': {
            showHeader: false,
            title: 'Tax Masters',
            description: 'Maintain effective-dated tax rates (GST masters) used by booking tax calculation',
            componentName: 'TaxMastersHost',
            actions: ['add', 'edit', 'delete', 'export'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Tax & Billing', 'Tax Masters']
          },
          'pms-charge-type': {
            showHeader: false,
            title: 'Charge Types',
            description: 'What a folio can be charged for: room, restaurant, laundry and others',
            componentName: 'ChargeTypesScreen',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Tax & Billing', 'Charge Types']
          },
          'org-entity': {
            showHeader: false,
            title: 'Entities',
            description: 'Legal entities, issued by the organisation master (Tenant Hub)',
            componentName: 'EntityMasterScreen',
            actions: ['add', 'edit'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Organisation', 'Entities']
          },
          'org-business-unit': {
            showHeader: false,
            title: 'Business Units',
            description: 'Business units (a property is one), issued by the organisation master',
            componentName: 'BusinessUnitMasterScreen',
            actions: ['add', 'edit'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Organisation', 'Business Units']
          },
          'org-financial-year': {
            showHeader: false,
            title: 'Financial Years',
            description: 'Financial years, issued by the organisation master',
            componentName: 'FinancialYearMasterScreen',
            actions: ['add', 'edit'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Organisation', 'Financial Years']
          },
          'pms-document-series': {
            showHeader: false,
            title: 'Document Series',
            description: 'Numbering of tax invoices, credit notes, receipt and refund vouchers',
            componentName: 'DocumentSeriesScreen',
            actions: ['edit', 'submit'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Tax & Billing', 'Document Series']
          },
          'pms-print-layouts': {
            showHeader: false,
            title: 'Print Layouts',
            description: 'How each property prints its documents: logo, header, terms and optional boxes',
            componentName: 'PrintLayoutsScreen',
            actions: ['edit'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Tax & Billing', 'Print Layouts']
          },
          'tax-charge-mapping': {
            showHeader: false,
            title: 'Charge Tax Mappings',
            description: 'Map room, meal plan and add-on charges to the tax master they are taxed under',
            componentName: 'ChargeTaxMappingsScreen',
            actions: ['add', 'edit'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Tax & Billing', 'Charge Tax Mappings']
          },
          'availability': {
            showHeader: false,
            title: 'Availability Calendar',
            description: 'Check room availability and inventory',
            componentName: 'AvailabilityScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Front Office', 'Availability']
          },
          'shortage-board': {
            showHeader: false,
            title: 'Shortage Board',
            description: 'Nights where committed demand exceeds inventory, and what is being done about it',
            componentName: 'ShortageBoardScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Front Office', 'Shortage Board']
          },
          'booking-list': {
            showHeader: false,
            title: 'Bookings',
            description: 'Manage reservations and booking records',
            componentName: 'BookingListScreen',
            actions: ['add', 'edit', 'delete', 'view'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Front Office', 'Bookings']
          },
          'front-office': {
            showHeader: false,
            title: 'Front Office',
            description: 'Check-ins, check-outs, walk-ins, room allocation and room status',
            componentName: 'FrontOfficeWorkspace',
            actions: ['view', 'add', 'checkin', 'checkout', 'mark-no-show', 'extend-stay', 'room-status', 'room-move'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Front Office']
          },
          'pms-inhouse': {
            showHeader: false,
            title: 'In-House Guests',
            description: 'Checked-in stays and their folios: charges, receipts and room charging',
            componentName: 'InHouseGuestsScreen',
            actions: ['view', 'post-charge', 'reverse-charge', 'receive-payment', 'posting-control', 'export',
              'post-recovery', 'post-disbursement', 'post-late-charge', 'waive-retention', 'relax-late-checkout'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Front Office', 'In-House Guests']
          },
          'pms-night-audit': {
            showHeader: false,
            title: 'Night Audit',
            description: "Close the business date: post the night's rooms, meal plans and add-ons",
            componentName: 'NightAuditScreen',
            actions: ['view', 'submit'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Front Office', 'Night Audit']
          },
          'fo-closing': {
            showHeader: false,
            title: 'Front Office Closing',
            description: "The desk's daily collections and close: cash counted, handed over, deposited and kept",
            componentName: 'FrontOfficeClosingScreen',
            actions: ['view', 'close', 'verify', 'reopen'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Front Office', 'Front Office Closing']
          },
          'pms-invoices': {
            showHeader: false,
            title: 'Invoices',
            description: 'Checkout invoices — one per payer of a stay; issue, view and print',
            componentName: 'InvoicesScreen',
            actions: ['view', 'issue', 'export', 'credit-note', 'debit-note', 'approve-note'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Front Office', 'Invoices']
          },
          'audit-corrections': {
            showHeader: false,
            title: 'Audit Corrections',
            description: 'Record a check-in or no-show missed on a date night audit has closed',
            componentName: 'AuditCorrectionsScreen',
            actions: ['view', 'checkin', 'mark-no-show'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Front Office', 'Audit Corrections']
          },
          'advance-payments': {
            showHeader: false,
            title: 'Advance Payments',
            description: 'Cashier reconciliation of advance payments across all bookings',
            componentName: 'AdvancePaymentsScreen',
            actions: ['edit', 'view', 'record-accounts-receipt'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk', 'cashier'],
            breadcrumb: ['Reports', 'Collections', 'Advance Payments']
          },
          'approval-inbox': {
            showHeader: false,
            title: 'Approval Inbox',
            description: 'Review and act on pending approval requests',
            componentName: 'ApprovalInboxScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Approvals', 'Approval Inbox']
          },
          'config-settings': {
            showHeader: false,
            title: 'Settings',
            description: 'Modules, booking, front office, billing and notification settings, system-wide or per property',
            componentName: 'ConfigSettingsScreen',
            actions: ['edit'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Settings']
          },
          'role-permissions': {
            showHeader: false,
            title: 'Role Permissions',
            description: 'Configure module visibility and action permissions for each Keycloak role',
            componentName: 'RolePermissionsScreen',
            actions: ['add', 'edit', 'delete', 'view'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Role Permissions']
          },
          'ledger-mapping': {
            showHeader: false,
            title: 'Fairbooks Ledger Mapping',
            description: 'Map bank accounts, payment modes and roles to Fairbooks ledgers for advance posting',
            componentName: 'LedgerMappingScreen',
            actions: ['add', 'edit', 'delete', 'export'],
            permissions: ['admin'],
            breadcrumb: ['Setup', 'Tax & Billing', 'Ledger Mapping']
          },
          'posting-register': {
            showHeader: false,
            title: 'Fairbooks Posting Register',
            description: 'Every advance handed to Fairbooks — what posted, what is stuck and why',
            componentName: 'PostingRegisterScreen',
            actions: ['view', 'retry', 'export'],
            permissions: ['admin'],
            breadcrumb: ['Reports', 'Collections', 'Posting Register']
          },

          // ── Dashboard (Phase 1 skeleton) ──────────────────────────────────
          'dashboard': {
            showHeader: false,
            title: 'Dashboard',
            description: 'Property KPIs, revenue, approvals, occupancy and partner performance',
            componentName: 'DashboardScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Dashboard']
          },

          // ── Reports (Phase 1 skeleton) ────────────────────────────────────
          'booking-register-report': {
            showHeader: false,
            title: 'Booking Register',
            description: 'Bookings by booking date, check-in, check-out or in house on a date — rate, pax, discount, net, advance and status',
            componentName: 'BookingRegisterReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Reports', 'Front Office', 'Booking Register']
          },
          'complimentary-report': {
            showHeader: false,
            title: 'Complimentary Bookings',
            description: 'Original rate, complimentary value, remarks and approval details',
            componentName: 'ComplimentaryReportScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Complimentary']
          },
          'discount-report': {
            showHeader: false,
            title: 'Discount Report',
            description: 'Discount amount, percentage, net rate and approval status',
            componentName: 'DiscountReportScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Discount']
          },
          'min-rate-violation-report': {
            showHeader: false,
            title: 'Minimum Rate Violation',
            description: 'Bookings priced below the rate plan minimum and their approvals',
            componentName: 'MinRateViolationReportScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Minimum Rate Violation']
          },
          'meal-plan-revenue-report': {
            showHeader: false,
            title: 'Meal Plan Revenue',
            description: 'Revenue by meal plan and impact of upgrades / downgrades',
            componentName: 'MealPlanRevenueReportScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Meal Plan Revenue']
          },
          'extra-pax-report': {
            showHeader: false,
            title: 'Extra Pax Report',
            description: 'Extra adult / child usage and the additional revenue earned',
            componentName: 'ExtraPaxReportScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Extra Pax']
          },
          'forward-occupancy-report': {
            showHeader: false,
            title: 'Forward Occupancy',
            description: 'Future occupancy, vacancy and category-wise availability',
            componentName: 'ForwardOccupancyReportScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Forward Occupancy']
          },
          'agent-corporate-report': {
            showHeader: false,
            title: 'Agent / Corporate Booking',
            description: 'Bookings and revenue by agent, corporate or rate plan',
            componentName: 'AgentCorporateReportScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Agent / Corporate']
          },
          'daily-monthly-summary-report': {
            showHeader: false,
            title: 'Daily / Monthly Summary',
            description: 'Total bookings, revenue, ADR, occupancy %, cancellations and adjustments',
            componentName: 'DailyMonthlySummaryReportScreen',
            actions: ['view'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Daily / Monthly Summary']
          },
          'occupancy-chart-report': {
            showHeader: false,
            title: 'Occupancy Chart',
            description: 'Room-by-room in-house position for a date with occupancy, gender and revenue KPIs',
            componentName: 'OccupancyChartReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Reports', 'Front Office', 'Room Occupancy Chart']
          },
          'collection-report': {
            showHeader: false,
            title: 'Collection',
            description: 'Receipts and refunds by mode for a period, with each voucher',
            componentName: 'CollectionReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Reports', 'Collections', 'Collection']
          },
          'guest-ledger-report': {
            showHeader: false,
            title: 'Guest Ledger',
            description: 'Posted vs Billed by business date: what guests owe on their folios',
            componentName: 'GuestLedgerReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Collections', 'Guest Ledger']
          },
          'fo-collection-report': {
            showHeader: false,
            title: 'Front Office Collection',
            description: 'Front Office receipts and refunds by method, kind and day, with each close',
            componentName: 'FoCollectionReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Collections', 'Front Office Collection']
          },
          'meal-forecast-report': {
            showHeader: false,
            title: 'Meal Forecast',
            description: 'Expected covers per date and meal: in-house, arriving, tentative, extra',
            componentName: 'MealForecastReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Meal Forecast']
          },
          'unused-meal-report': {
            showHeader: false,
            title: 'Unused Package Meals',
            description: 'Plan meals guests did not take, by stay and by plan',
            componentName: 'UnusedMealReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Unused Package Meals']
          },
          'waiver-report': {
            showHeader: false,
            title: 'Relaxations & Waivers',
            description: 'Stay charges charged below the entitlement, with who allowed it and why',
            componentName: 'WaiverReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin', 'manager'],
            breadcrumb: ['Reports', 'Front Office', 'Relaxations & Waivers']
          },
          'checkin-details-report': {
            showHeader: false,
            title: 'Check-in Details',
            description: 'Registration register with GRC numbers, guest identity, gender mix and tariffs',
            componentName: 'CheckinDetailsReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin', 'manager', 'supervisor', 'frontdesk'],
            breadcrumb: ['Reports', 'Front Office', 'Check-in Details']
          },
          'checkout-register': {
            showHeader: false,
            title: 'Checkout Register',
            description: 'Checkouts with booked, advance, collected and balance, who settles and the invoice status',
            componentName: 'CheckoutRegisterReportScreen',
            actions: ['view', 'export'],
            permissions: ['admin', 'manager', 'supervisor'],
            breadcrumb: ['Reports', 'Front Office', 'Checkout Register']
          },
          'user-management': {
            showHeader: true,
            title: 'User Management',
            description: 'Manage system users and their access',
            componentName: '',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin'],
            breadcrumb: ['Administration', 'User Management']
          },
          'role-management': {
            showHeader: true,
            title: 'Role Management',
            description: 'Manage user roles and permissions',
            componentName: '',
            actions: ['add', 'edit', 'delete'],
            permissions: ['admin'],
            breadcrumb: ['Administration', 'Role Management']
          },
          'audit-logs': {
            showHeader: true,
            title: 'Audit Logs',
            description: 'View system audit trail',
            componentName: '',
            actions: ['view', 'export'],
            permissions: ['admin', 'auditor'],
            breadcrumb: ['Administration', 'Audit Logs']
          }
        };

        // ============================================================================
        // ROLE-PERMISSION GATE — fetch /RolePermissions/by-roles, filter menu + actions
        // ============================================================================
        // Mirrors the prima-web login flow. Without this step, every authenticated
        // user sees the full hardcoded menu regardless of role. With it:
        //   1. We extract the user's CRS role(s) from the JWT (resource_access.crs.roles[],
        //      realm_access.roles[], flat role/roles claims, plus authData.userRole).
        //   2. We pick the highest-priority crs_* role. If none, we reject login.
        //   3. We call /by-roles to get the merged visibleModules + moduleActions.
        //   4. We filter the hardcoded navigationMenu by visibleModules and override
        //      each module's actions array. The result is what gets written to
        //      localStorage for main_layout to consume.
        const userRoles = collectUserRoles(authData.userRole);

        // Ensure the DB-driven role catalog is loaded BEFORE calling
        // pickHighestPriorityRole — otherwise roles added via the admin UI
        // would fall through to the fallback baseline list and be treated as
        // "unknown". hydrateKnownRoles is a no-op when the cache is still
        // fresh (15-min TTL), so this is essentially free after the first
        // login. Non-fatal on failure — the fallback covers the four seeded
        // roles so baseline logins still work.
        await hydrateKnownRoles();

        const primaryRole = pickHighestPriorityRole(userRoles);

        if (!primaryRole) {
          const knownList = getKnownRoleDisplayList();
          const knownAsText = knownList.length > 0
            ? knownList.join(', ')
            : 'crs_admin, crs_booking_agent, crs_user, or crs_system';
          setError(
            'Your account is not assigned any CRS role. ' +
            `Please contact your administrator to assign one of: ${knownAsText}.`
          );
          tokenManager.clearAll();
          return;
        }

        let merged: RolePermissionMerged | null = null;
        try {
          // Pass ALL the user's CRS roles so the backend unions their permissions.
          // Filter against the current DB-driven catalog so custom roles are
          // included in the union alongside the four baseline codes.
          const known = new Set(getRolePriority().map((c) => c.toLowerCase()));
          const crsRoles = userRoles.filter((r) => known.has(r.toLowerCase()));
          const response = await rolePermissionService.getByRoles(crsRoles);
          if (response.success && response.data) {
            merged = response.data;
          }
        } catch {
          // Non-fatal — fall through to the system-role bootstrap below if applicable.
        }

        // No client-side fallback — permissions come exclusively from
        // admin.tbl_role_permissions. If the seed row for the user's role is
        // missing or empty, login is rejected. Recovery is via DB seed:
        //   psql -f crs-db/seed/010_seed_admin_role_permissions.sql
        if (!merged || merged.visibleModules.length === 0) {
          setError(
            `Your role "${primaryRole}" has no modules configured. ` +
            'Please contact your administrator to configure permissions in the Role Permissions screen.'
          );
          tokenManager.clearAll();
          return;
        }

        // Build the role-filtered navigation + modules config. A module switched off for every
        // property drops out of the sidebar only (settings cleanup C3); the role keeps it.
        const switches = await configService.getModuleSwitches().catch(() => []);
        const filteredNavigation = filterNavigationMenu(
          navigationMenu as ReadonlyArray<NavMenuSection>,
          menuModulesForSwitches(merged.visibleModules, switches),
        );
        const filteredModules = applyRoleActions(modulesConfig as ModulesConfigMap, merged);

        // Cache merged permissions for components/hooks (e.g. PermissionGate).
        localStorage.setItem(STORAGE_KEYS.RBAC_ROLES, JSON.stringify(userRoles));
        localStorage.setItem(STORAGE_KEYS.RBAC_VISIBLE_MODULES, JSON.stringify(merged.visibleModules));
        localStorage.setItem(STORAGE_KEYS.RBAC_MODULE_ACTIONS, JSON.stringify(merged.moduleActions));

        localStorage.setItem('NAVIGATION_MENU', JSON.stringify(filteredNavigation));
        localStorage.setItem('MODULES_CONFIG', JSON.stringify(filteredModules));
        localStorage.setItem('APP_CONFIG', JSON.stringify(appConfig));

        const proceed = () => {
          navigate('/home', { replace: true });
          onLoginSuccess?.(user);
        };
        await pickDineOutlet(merged.visibleModules);
        proceed();
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || 'Login failed. Please try again.';
        setError(msg);
      }
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation
    if (name === 'email') {
      validateEmail(value);
    } else if (name === 'password') {
      validatePassword(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="lg-root">
      <style>{`
        .lg-root {
          display: flex;
          height: 100vh;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", Roboto, sans-serif;
          --lg-dark: #0f172a;
          --lg-mid: #1e293b;
          --lg-teal: #14b8a6;
          --lg-teal-focus: rgba(20, 184, 166, 0.15);
          --lg-teal-ring: rgba(20, 184, 166, 0.25);
          --lg-slate-50: #f8fafc;
          --lg-slate-100: #f1f5f9;
          --lg-slate-200: #e2e8f0;
          --lg-slate-400: #94a3b8;
          --lg-slate-500: #64748b;
          --lg-slate-700: #334155;
          --lg-slate-900: #0f172a;
          --lg-danger: #ef4444;
          --lg-danger-bg: #fef2f2;
          --lg-danger-border: #fecaca;
        }

        .lg-root * {
          box-sizing: border-box;
        }

        /* ── BRAND PANEL ─────────────────────────────────────────────── */
        .lg-brand {
          width: 44%;
          flex-shrink: 0;
          background: linear-gradient(155deg, var(--lg-dark) 0%, var(--lg-mid) 100%);
          display: flex;
          flex-direction: column;
          padding: 2.5rem 3rem;
          position: relative;
          overflow: hidden;
          box-shadow: 8px 0 40px rgba(0, 0, 0, 0.3);
          z-index: 1;
        }

        .lg-brand-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          flex-shrink: 0;
          z-index: 1;
        }

        .lg-brand-logo-img {
          height: 88px;
          width: auto;
          max-width: 440px;
          object-fit: contain;
          display: block;
          /* syut_logo.png is dark teal — invert to pure white so it stands
             out on the dark sidebar gradient. brightness(0) flattens the
             image to solid black, then invert(1) flips black → white. */
          filter: brightness(0) invert(1);
        }

        .lg-brand-mark {
          width: 2.375rem;
          height: 2.375rem;
          background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.0625rem;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.04em;
          flex-shrink: 0;
          user-select: none;
          box-shadow: 0 2px 12px rgba(20, 184, 166, 0.5);
        }

        .lg-brand-name {
          font-size: 0.9375rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.88);
          letter-spacing: -0.01em;
        }

        .lg-graphic {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2.5rem 0;
          z-index: 1;
        }

        .lg-graphic svg {
          width: 100%;
          max-width: 360px;
          height: auto;
        }

        .lg-hub-ring {
          animation: lg-ring-pulse 4s ease-in-out infinite;
        }
        .lg-hub-ring-2 {
          animation: lg-ring-pulse 4s ease-in-out infinite 1.5s;
        }
        .lg-hub-ring-3 {
          animation: lg-ring-pulse 4s ease-in-out infinite 0.8s;
        }
        .lg-hub-ring-4 {
          animation: lg-ring-pulse 4s ease-in-out infinite 2.2s;
        }
        .lg-flow-a {
          stroke-dasharray: 6 10;
          animation: lg-flow 2.4s linear infinite;
        }
        .lg-flow-b {
          stroke-dasharray: 6 10;
          animation: lg-flow 3.1s linear infinite 0.9s;
        }
        .lg-flow-c {
          stroke-dasharray: 6 10;
          animation: lg-flow-rev 2.8s linear infinite 0.4s;
        }

        .lg-taglines {
          z-index: 1;
          flex-shrink: 0;
        }

        .lg-tagline-h {
          font-size: 1.75rem;
          font-weight: 700;
          color: #ffffff;
          line-height: 1.2;
          letter-spacing: -0.035em;
          margin: 0 0 0.625rem;
        }

        .lg-tagline-p {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.38);
          margin: 0;
          font-weight: 400;
          letter-spacing: 0.005em;
        }

        /* ── STATS STRIP ─────────────────────────────────────────────── */
        .lg-stats {
          display: flex;
          align-items: stretch;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 0.75rem;
          margin-bottom: 1.5rem;
          flex-shrink: 0;
          z-index: 1;
          overflow: hidden;
        }

        .lg-stat-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 0.875rem 0.5rem;
          gap: 0.25rem;
        }

        .lg-stat-item + .lg-stat-item {
          border-left: 1px solid rgba(255, 255, 255, 0.08);
        }

        .lg-stat-value {
          font-size: 1.375rem;
          font-weight: 800;
          color: var(--lg-teal);
          letter-spacing: -0.03em;
          line-height: 1;
        }

        .lg-stat-label {
          font-size: 0.5625rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.35);
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-align: center;
        }

        /* ── FEATURE CARDS ───────────────────────────────────────────── */
        .lg-features {
          z-index: 1;
          flex-shrink: 0;
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .lg-feature-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.07);
          border-radius: 0.625rem;
          padding: 0.625rem 0.75rem;
        }

        .lg-feature-icon {
          width: 2rem;
          height: 2rem;
          border-radius: 0.5rem;
          background: rgba(20, 184, 166, 0.12);
          border: 1px solid rgba(20, 184, 166, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: var(--lg-teal);
        }

        .lg-feature-text {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
          min-width: 0;
        }

        .lg-feature-title {
          font-size: 0.8125rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.82);
          letter-spacing: 0.005em;
          line-height: 1.3;
        }

        .lg-feature-desc {
          font-size: 0.6875rem;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.35);
          letter-spacing: 0.005em;
          line-height: 1.4;
        }

        .lg-brand-footer {
          margin-top: 2rem;
          flex-shrink: 0;
          z-index: 1;
        }

        .lg-version {
          font-size: 0.6875rem;
          color: rgba(255, 255, 255, 0.18);
          font-weight: 500;
          letter-spacing: 0.03em;
          display: block;
          margin-bottom: 0.375rem;
        }

        .lg-brand-website {
          font-size: 0.6875rem;
          font-weight: 600;
          color: rgba(20, 184, 166, 0.65);
          text-decoration: none;
          letter-spacing: 0.02em;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          transition: color 0.2s;
        }

        .lg-brand-website:hover {
          color: rgba(20, 184, 166, 1);
        }

        /* ── APP IDENTITY ────────────────────────────────────────────── */
        .lg-app-identity {
          margin-bottom: 1.875rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid var(--lg-slate-200);
        }

        .lg-app-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          background: rgba(20, 184, 166, 0.1);
          border: 1px solid rgba(20, 184, 166, 0.22);
          border-radius: 2rem;
          padding: 0.2rem 0.625rem;
          font-size: 0.625rem;
          font-weight: 700;
          color: #0d9488;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .lg-app-badge-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #14b8a6;
          flex-shrink: 0;
        }

        .lg-app-name {
          font-size: 1.3125rem;
          font-weight: 800;
          color: var(--lg-slate-900);
          letter-spacing: -0.03em;
          line-height: 1.2;
          margin: 0 0 0.5rem;
        }

        /* ── FORM PANEL ──────────────────────────────────────────────── */
        .lg-form-panel {
          flex: 1;
          background: #fafbff;
          background-image: radial-gradient(circle, rgba(148, 163, 184, 0.2) 1px, transparent 1px);
          background-size: 22px 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2.5rem 2rem;
          overflow-y: auto;
        }

        .lg-form-inner {
          width: 100%;
          max-width: 400px;
        }

        .lg-form-h1 {
          font-size: 1.625rem;
          font-weight: 700;
          color: var(--lg-slate-900);
          letter-spacing: -0.035em;
          margin: 0 0 0.375rem;
          line-height: 1.2;
        }

        .lg-form-sub {
          font-size: 0.875rem;
          color: var(--lg-slate-500);
          margin: 0 0 2rem;
          font-weight: 400;
          line-height: 1.5;
        }

        /* ── FIELDS ──────────────────────────────────────────────────── */
        .lg-field {
          margin-bottom: 1.125rem;
        }

        .lg-label {
          display: block;
          font-size: 0.6875rem;
          font-weight: 700;
          color: var(--lg-slate-700);
          margin-bottom: 0.4rem;
          letter-spacing: 0.07em;
          text-transform: uppercase;
        }

        .lg-label-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.4rem;
        }

        .lg-label-row .lg-label {
          margin-bottom: 0;
        }

        .lg-input-wrap {
          position: relative;
        }

        .lg-input-icon {
          position: absolute;
          left: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--lg-slate-400);
          display: flex;
          align-items: center;
          pointer-events: none;
          transition: color 0.2s;
        }

        .lg-input {
          width: 100%;
          padding: 0.6875rem 0.875rem 0.6875rem 2.625rem;
          font-size: 0.875rem;
          font-family: inherit;
          font-weight: 500;
          color: var(--lg-slate-900);
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 0.625rem;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          appearance: none;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .lg-input::placeholder {
          color: var(--lg-slate-400);
          font-weight: 400;
        }

        .lg-input:focus {
          border-color: var(--lg-teal);
          box-shadow: 0 0 0 3px var(--lg-teal-focus), 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .lg-input:focus + .lg-input-icon,
        .lg-input-wrap:focus-within .lg-input-icon {
          color: var(--lg-teal);
        }

        .lg-input.has-error {
          border-color: var(--lg-danger);
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .lg-input-pw {
          padding-right: 2.875rem;
        }

        .lg-pw-toggle {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: var(--lg-slate-400);
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.375rem;
          transition: color 0.2s;
          line-height: 0;
        }

        .lg-pw-toggle:hover {
          color: var(--lg-slate-700);
        }

        .lg-field-err {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.75rem;
          color: var(--lg-danger);
          margin-top: 0.375rem;
          font-weight: 500;
        }

        .lg-forgot {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--lg-teal);
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          transition: opacity 0.2s;
          font-family: inherit;
        }

        .lg-forgot:hover {
          opacity: 0.7;
        }

        /* ── SELECT ──────────────────────────────────────────────────── */
        .lg-select {
          width: 100%;
          padding: 0.6875rem 0.875rem;
          font-size: 0.875rem;
          font-family: inherit;
          font-weight: 500;
          color: var(--lg-slate-900);
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 0.625rem;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          cursor: pointer;
          margin-bottom: 0.875rem;
          appearance: none;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .lg-select:focus {
          border-color: var(--lg-teal);
          background: #ffffff;
          box-shadow: 0 0 0 3px var(--lg-teal-focus);
        }

        /* ── ERROR ALERT ─────────────────────────────────────────────── */
        .lg-error-alert {
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
          background: var(--lg-danger-bg);
          border: 1.5px solid var(--lg-danger-border);
          border-radius: 0.625rem;
          padding: 0.75rem 1rem;
          margin-top: 1rem;
          margin-bottom: 0.25rem;
          color: #dc2626;
          font-size: 0.8125rem;
          font-weight: 500;
          line-height: 1.45;
        }

        .lg-error-icon {
          flex-shrink: 0;
          margin-top: 1px;
        }

        /* ── SUBMIT BUTTON ───────────────────────────────────────────── */
        .lg-submit {
          width: 100%;
          padding: 0.8125rem 1.25rem;
          font-size: 0.9375rem;
          font-weight: 700;
          font-family: inherit;
          letter-spacing: 0.01em;
          border: none;
          border-radius: 0.625rem;
          background: var(--lg-teal);
          color: var(--lg-dark);
          cursor: pointer;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1.375rem;
          position: relative;
          overflow: hidden;
        }

        .lg-submit::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 60%);
          border-radius: inherit;
          pointer-events: none;
        }

        .lg-submit:hover:not(:disabled) {
          background: #0d9488;
          box-shadow: 0 8px 28px rgba(20, 184, 166, 0.4);
          transform: translateY(-1px);
        }

        .lg-submit:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 4px 12px rgba(20, 184, 166, 0.3);
        }

        .lg-submit:disabled {
          background: var(--lg-slate-200);
          color: var(--lg-slate-400);
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .lg-submit:disabled::after {
          display: none;
        }

        .lg-spinner {
          width: 17px;
          height: 17px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: lg-spin 0.75s linear infinite;
          flex-shrink: 0;
        }

        /* ── TENANT SECTION ──────────────────────────────────────────── */
        .lg-tenant-section {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid var(--lg-slate-200);
        }

        .lg-back-btn {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--lg-teal);
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          margin-top: 0.25rem;
          font-family: inherit;
          transition: opacity 0.2s;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .lg-back-btn:hover {
          opacity: 0.7;
        }

        /* ── FORM FOOTER ─────────────────────────────────────────────── */
        .lg-form-footer {
          margin-top: 2rem;
          padding-top: 1.25rem;
          border-top: 1px solid var(--lg-slate-100);
        }

        .lg-footer-links {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-bottom: 0.75rem;
          flex-wrap: wrap;
        }

        .lg-footer-link {
          font-size: 0.75rem;
          color: var(--lg-slate-400);
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }

        .lg-footer-link:hover {
          color: var(--lg-teal);
        }

        .lg-footer-link-website {
          color: var(--lg-teal);
          font-weight: 600;
        }

        .lg-footer-link-website:hover {
          color: #0d9488;
        }

        .lg-copyright {
          text-align: center;
          font-size: 0.6875rem;
          color: var(--lg-slate-400);
          margin: 0;
          font-weight: 400;
        }

        /* ── ANIMATIONS ──────────────────────────────────────────────── */
        @keyframes lg-spin {
          to { transform: rotate(360deg); }
        }

        @keyframes lg-ring-pulse {
          0%, 100% { opacity: 0.12; }
          50%       { opacity: 0.28; }
        }

        @keyframes lg-flow {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: -32; }
        }

        @keyframes lg-flow-rev {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: 32; }
        }

        @keyframes lg-enter {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .lg-form-inner {
          animation: lg-enter 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        /* ── RESPONSIVE ──────────────────────────────────────────────── */
        /* Once the brand panel narrows below the desktop layout, the
           three-column stats strip can no longer fit each value+label
           on a single line. Stack the stats vertically (value left,
           label right) so each gets the full panel width. */
        @media (max-width: 1100px) {
          .lg-stats { flex-direction: column; }
          .lg-stat-item {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 0.625rem 0.875rem;
            gap: 0.5rem;
          }
          .lg-stat-item + .lg-stat-item {
            border-left: none;
            border-top: 1px solid rgba(255, 255, 255, 0.08);
          }
          .lg-stat-value {
            font-size: 0.9375rem;
            letter-spacing: -0.015em;
            text-align: left;
            white-space: nowrap;
          }
          .lg-stat-label {
            font-size: 0.5rem;
            letter-spacing: 0.06em;
            text-align: right;
            white-space: nowrap;
          }
        }

        @media (max-width: 900px) {
          .lg-brand {
            width: 40%;
            padding: 1.75rem 1.5rem;
          }
          .lg-tagline-h { font-size: 1.5rem; }
          /* Drop the brand-panel padding and feature description size so
             the column doesn't push the form panel below the fold. */
          .lg-feature-desc { display: none; }
          .lg-feature-item { padding: 0.5rem 0.625rem; }
          .lg-brand-footer { margin-top: 1rem; }
        }

        /* Tablet portrait + phone landscape: collapse to a single column
           with the brand reduced to a thin top strip. The 820px threshold
           catches iPad portrait (768px), most Android tablets, and any
           desktop window dragged narrower than ~half of a 1440p screen. */
        @media (max-width: 820px) {
          .lg-root {
            flex-direction: column;
            height: auto;
            min-height: 100vh;
            overflow-x: hidden;
          }

          .lg-brand {
            width: 100%;
            flex-direction: row;
            align-items: center;
            padding: 0.875rem 1.375rem;
            flex-shrink: 0;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
          }

          .lg-graphic,
          .lg-stats,
          .lg-features,
          .lg-taglines,
          .lg-brand-footer {
            display: none;
          }

          .lg-form-panel {
            flex: 1;
            align-items: flex-start;
            padding: 2rem 1.5rem 2.5rem;
          }

          .lg-form-inner {
            max-width: 100%;
          }
        }

        @media (max-width: 480px) {
          .lg-brand { padding: 0.625rem 1rem; }
          .lg-brand-logo-img { height: 50px; }
          .lg-form-panel { padding: 1.25rem 1rem 1.75rem; }
          .lg-form-h1 { font-size: 1.375rem; }
          .lg-form-sub { font-size: 0.8125rem; margin-bottom: 1.25rem; }
          .lg-app-name { font-size: 1.125rem; }
          .lg-app-identity { margin-bottom: 1.25rem; padding-bottom: 1rem; }
          .lg-footer-links { gap: 0.5rem; }
          .lg-footer-link { font-size: 0.6875rem; }
        }

        @media (max-width: 360px) {
          .lg-brand-logo-img { height: 42px; }
          .lg-form-h1 { font-size: 1.25rem; }
        }
      `}</style>

      {/* ── LEFT BRAND PANEL ────────────────────────────────────────── */}
      <aside className="lg-brand">
        <div className="lg-brand-logo">
          <img
            src={`${process.env.PUBLIC_URL}/Logos/syut_logo.png`}
            alt="Syut Technologies"
            className="lg-brand-logo-img"
          />
        </div>

        {/* Abstract node-network graphic */}
        <div className="lg-graphic">
          <svg viewBox="0 0 460 340" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
              <filter id="lg-glow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* ── Node-to-node lines (muted) */}
            <line x1="40" y1="40" x2="142" y2="55" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="320" y1="38" x2="428" y2="122" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="428" y1="122" x2="444" y2="178" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="444" y1="178" x2="402" y2="232" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="402" y1="232" x2="300" y2="212" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="220" y1="232" x2="234" y2="312" stroke="rgba(148,163,184,0.16)" strokeWidth="1"/>
            <line x1="234" y1="312" x2="356" y2="284" stroke="rgba(148,163,184,0.16)" strokeWidth="1"/>
            <line x1="62" y1="300" x2="234" y2="312" stroke="rgba(148,163,184,0.14)" strokeWidth="1"/>

            {/* ── Hub-connected lines (teal) */}
            <line x1="82" y1="92" x2="40" y2="40" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="82" y1="92" x2="142" y2="55" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="82" y1="92" x2="94" y2="180" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="142" y1="55" x2="192" y2="114" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="320" y2="38" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="428" y2="122" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="252" y2="162" stroke="rgba(20,184,166,0.18)" strokeWidth="1"/>
            <line x1="192" y1="114" x2="252" y2="162" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="252" y2="162" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="356" y2="284" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="156" y2="264" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="252" y1="162" x2="220" y2="232" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="220" y2="232" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="94" y2="180" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="62" y2="300" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="234" y2="312" stroke="rgba(20,184,166,0.18)" strokeWidth="1"/>

            {/* ── Animated flow lines */}
            <line x1="82" y1="92" x2="252" y2="162" stroke="rgba(20,184,166,0.55)" strokeWidth="1.5" className="lg-flow-a"/>
            <line x1="252" y1="162" x2="156" y2="264" stroke="rgba(20,184,166,0.55)" strokeWidth="1.5" className="lg-flow-b"/>
            <line x1="378" y1="78" x2="252" y2="162" stroke="rgba(20,184,166,0.45)" strokeWidth="1.5" className="lg-flow-c"/>

            {/* ── Regular nodes */}
            <circle cx="40"  cy="40"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="142" cy="55"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="192" cy="114" r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="320" cy="38"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="428" cy="122" r="3"   fill="rgba(148,163,184,0.4)"/>
            <circle cx="300" cy="212" r="3.5" fill="rgba(148,163,184,0.45)"/>
            <circle cx="402" cy="232" r="3"   fill="rgba(148,163,184,0.38)"/>
            <circle cx="94"  cy="180" r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="220" cy="232" r="3.5" fill="rgba(148,163,184,0.45)"/>
            <circle cx="62"  cy="300" r="2.5" fill="rgba(148,163,184,0.32)"/>
            <circle cx="234" cy="312" r="2.5" fill="rgba(148,163,184,0.32)"/>
            <circle cx="356" cy="284" r="3"   fill="rgba(148,163,184,0.38)"/>
            <circle cx="444" cy="178" r="2.5" fill="rgba(148,163,184,0.32)"/>

            {/* ── Hub glow rings (animated pulse) */}
            <circle cx="82"  cy="92"  r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring"/>
            <circle cx="252" cy="162" r="18" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-2"/>
            <circle cx="378" cy="78"  r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-3"/>
            <circle cx="156" cy="264" r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-4"/>

            {/* ── Hub nodes (teal, glowing) */}
            <circle cx="82"  cy="92"  r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="252" cy="162" r="7.5" fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="378" cy="78"  r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="156" cy="264" r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
          </svg>
        </div>

        {/* Stats strip */}
        <div className="lg-stats">
          <div className="lg-stat-item">
            <span className="lg-stat-value">Multi-Property</span>
            <span className="lg-stat-label">Unified Management</span>
          </div>
          <div className="lg-stat-item">
            <span className="lg-stat-value">Live</span>
            <span className="lg-stat-label">Booking Engine</span>
          </div>
          <div className="lg-stat-item">
            <span className="lg-stat-value">Cloud-native</span>
            <span className="lg-stat-label">Always Available</span>
          </div>
        </div>

        {/* Feature cards */}
        <div className="lg-features">
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Role-based Access Control</span>
              <span className="lg-feature-desc">Fine-grained permissions per user and team</span>
            </div>
          </div>
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Multi-tenant Architecture</span>
              <span className="lg-feature-desc">Isolated, secure data per organization</span>
            </div>
          </div>
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Real-time Data Sync</span>
              <span className="lg-feature-desc">Live updates across all connected clients</span>
            </div>
          </div>
        </div>

        <div className="lg-taglines">
          <p className="lg-tagline-h">One platform.<br/>Every workflow.</p>
          <p className="lg-tagline-p">Bringing technology to people.</p>
        </div>

        <div className="lg-brand-footer">
          <span className="lg-version">© {new Date().getFullYear()} Syut Technologies</span>
          <a href="https://syuttechnologies.com" target="_blank" rel="noopener noreferrer" className="lg-brand-website">
            syuttechnologies.com
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>
      </aside>

      {/* ── RIGHT FORM PANEL ────────────────────────────────────────── */}
      <main className="lg-form-panel">
        <div className="lg-form-inner">
          {/* App identity */}
          <div className="lg-app-identity">
            <p className="lg-app-name">Central Reservation System</p>
            <div className="lg-app-badge">
              <span className="lg-app-badge-dot"/>
              All-in-One Hospitality Platform
            </div>
          </div>

          <h1 className="lg-form-h1">Welcome back</h1>
          <p className="lg-form-sub">Sign in to your account to continue</p>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>

            {/* User ID */}
            <div className="lg-field">
              <label htmlFor="lg-email" className="lg-label">User ID</label>
              <div className="lg-input-wrap">
                <span className="lg-input-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
                <input
                  type="text"
                  id="lg-email"
                  name="email"
                  value={credentials.email}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  disabled={isPending}
                  placeholder="Enter your user ID"
                  aria-label="User ID"
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? 'lg-email-err' : undefined}
                  className={`lg-input${emailError ? ' has-error' : ''}`}
                  autoComplete="username"
                />
              </div>
              {emailError && (
                <p id="lg-email-err" className="lg-field-err">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10" opacity="0.2"/>
                    <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {emailError}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="lg-field" style={{ marginBottom: '0.5rem' }}>
              <div className="lg-label-row">
                <label htmlFor="lg-password" className="lg-label">Password</label>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => alert('Forgot password functionality - Connect to your password reset system')}
                  className="lg-forgot"
                >
                  Forgot?
                </button>
              </div>
              <div className="lg-input-wrap">
                <span className="lg-input-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="lg-password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  disabled={isPending}
                  placeholder="Enter your password"
                  aria-label="Password"
                  aria-invalid={!!passwordError}
                  aria-describedby={passwordError ? 'lg-pw-err' : undefined}
                  className={`lg-input lg-input-pw${passwordError ? ' has-error' : ''}`}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="lg-pw-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              {passwordError && (
                <p id="lg-pw-err" className="lg-field-err">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10" opacity="0.2"/>
                    <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {passwordError}
                </p>
              )}
            </div>

            {/* Tenant selection — tenant-hub mode step 2 */}
            {getAuthMode() === 'tenant-hub' && authStep === 'tenant-select' && (
              <div className="lg-tenant-section">
                <div className="lg-field">
                  <label className="lg-label">Select Tenant</label>
                  <select
                    value={selectedTenantId}
                    onChange={(e) => {
                      setSelectedTenantId(e.target.value);
                      setSelectedAppId('');
                      const tenant = accessibleTenants.find(t => t.tenantId === e.target.value);
                      if (tenant?.applications.length === 1) {
                        setSelectedAppId(tenant.applications[0].applicationId);
                      }
                    }}
                    className="lg-select"
                  >
                    <option value="">— Select tenant —</option>
                    {accessibleTenants.map(t => (
                      <option key={t.tenantId} value={t.tenantId}>
                        {t.tenantDisplayName || t.tenantName} ({t.tenantCode})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedTenantId && (() => {
                  const tenant = accessibleTenants.find(t => t.tenantId === selectedTenantId);
                  const apps = tenant?.applications || [];
                  if (apps.length <= 1) return null;
                  return (
                    <div className="lg-field">
                      <label className="lg-label">Select Application</label>
                      <select
                        value={selectedAppId}
                        onChange={(e) => setSelectedAppId(e.target.value)}
                        className="lg-select"
                        style={{ marginBottom: 0 }}
                      >
                        <option value="">— Select application —</option>
                        {apps.map(a => (
                          <option key={a.applicationId} value={a.applicationId}>
                            {a.applicationName} ({a.applicationCode})
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                <button
                  type="button"
                  onClick={() => {
                    setAuthStep('credentials');
                    setAccessibleTenants([]);
                    setSelectedTenantId('');
                    setSelectedAppId('');
                    setError('');
                  }}
                  className="lg-back-btn"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                  Back to credentials
                </button>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="lg-error-alert">
                <svg className="lg-error-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="10" opacity="0.2"/>
                  <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {error}
              </div>
            )}

            {/* Sign In button */}
            <button
              type="submit"
              disabled={isPending || !!emailError || !!passwordError}
              className="lg-submit"
            >
              {isPending ? (
                <>
                  <div className="lg-spinner"/>
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="lg-form-footer">
            <div className="lg-footer-links">
              <a href="#privacy" className="lg-footer-link">Privacy Policy</a>
              <a href="#terms" className="lg-footer-link">Terms of Service</a>
              <a href="#support" className="lg-footer-link">Support</a>
              <a href="https://syuttechnologies.com" target="_blank" rel="noopener noreferrer" className="lg-footer-link lg-footer-link-website">
                syuttechnologies.com
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:'3px',verticalAlign:'middle'}}>
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            </div>
            <p className="lg-copyright">© {new Date().getFullYear()} Syut Technologies. All rights reserved.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginInterface;
